// src/pages/admin/workspace/WorkspaceHome.jsx
//
// Redesigned Campus Connect workspace — the Dashboard Selector (/admin/dashboard-selector).
//
// UI-only redesign. The access model is the original DashboardSelector's and is
// reused, not reimplemented (see useWorkspaceData.js):
//  - internal dashboards: resolveDashboardAccess() + settings.dashboardRegistry
//  - everything else:     getWorkspaceItems(role, email)
//  - single-dashboard redirect, launch handler (openItem), favourites, sidebar
//    pin preference, Public Forms modal, Analytics view and Echo are the original
//    behaviours.
// Profile and logout reuse the shared ProfileModal and AuthContext.logout.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { ArrowRight, SlidersHorizontal } from "lucide-react";
import EchoOrb from "../../../components/EchoOrb";
import EchoModal from "../../../components/EchoModal";
import ProfileModal from "../../../components/ProfileModal";
import { useAuth } from "../../../context/AuthContext";
import useSystemSettings from "../../../hooks/useSystemSettings";
import { readSidebarPinned, writeSidebarPinned } from "../../../config/campusPortals";
import AdvancedAnalyticsPage from "../AdvancedAnalyticsPage";
import { PublicFormsModal } from "../DashboardSelector";
import useWorkspaceData from "./useWorkspaceData";
import useWorkspaceTheme from "./useWorkspaceTheme";
import { CATEGORY, CATEGORY_LABELS } from "./workspaceCategories";
import { CATEGORY_ICONS } from "./categoryIcons";
import SidebarNav from "./components/SidebarNav";
import WorkspaceHeader from "./components/WorkspaceHeader";
import WelcomeBanner from "./components/WelcomeBanner";
import WorkspaceSections from "./components/WorkspaceSections";
import MobileBottomNav, { MORE_CATEGORY_IDS, MoreSheet } from "./components/MobileBottomNav";
import "./workspace.css";

export const humanizeRole = (role) =>
  String(role || "")
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");

const WorkspaceHome = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const { settings } = useSystemSettings();
  const { theme, toggleTheme } = useWorkspaceTheme();

  const data = useWorkspaceData(currentUser, settings);
  const { role, email, redirectTo, items, favoritableItems, quickAccessItems, favoriteIds, canFavorite, toggleFavorite } = data;
  const isAdmin = role === "admin";
  const userName = currentUser?.name || "User";

  // UI state (never any auth data)
  const [activeCategory, setActiveCategory] = useState(CATEGORY.ALL);
  const [query, setQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [manage, setManage] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [sidebarPinned, setSidebarPinned] = useState(readSidebarPinned);
  const moreButtonRef = useRef(null);

  // Existing modal / view state (same as the original selector)
  const [showPublicForms, setShowPublicForms] = useState(false);
  const [showEcho, setShowEcho] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);

  // Profile: same pattern as the Guest Room / Venue dashboards — a local copy of
  // the user that the shared ProfileModal updates through its onUpdate callback.
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileUser, setProfileUser] = useState(currentUser);
  useEffect(() => {
    setProfileUser(currentUser);
  }, [currentUser]);

  const closeMore = useCallback(() => setMoreOpen(false), []);

  // ─── Navigation (the original openItem, unchanged) ────────────────────────
  const openItem = useCallback(
    (item) => {
      const { target } = item;
      switch (target.type) {
        case "external":
          // New tab keeps the workspace open. The external portal owns its own
          // session; nothing is passed to it from here.
          window.open(target.url, "_blank", "noopener,noreferrer");
          break;
        case "route":
          navigate(target.path);
          break;
        case "redirect":
          // Separate app on the same domain (not in this SPA's router).
          window.location.href = target.path;
          break;
        case "action":
          if (target.action === "showAnalytics") setShowAnalytics(true);
          if (target.action === "showPublicForms") setShowPublicForms(true);
          break;
        default:
          break;
      }
    },
    [navigate]
  );

  const toggleSidebarPinned = () => {
    const next = !sidebarPinned;
    setSidebarPinned(next);
    writeSidebarPinned(next);
  };

  // ─── Category navigation (presentation only) ──────────────────────────────
  const counts = useMemo(() => {
    const result = { [CATEGORY.ALL]: items.length };
    items.forEach((item) => {
      result[item.category] = (result[item.category] || 0) + 1;
    });
    result[CATEGORY.FAVOURITES] = quickAccessItems.length;
    return result;
  }, [items, quickAccessItems]);

  // Dashboards is always offered (the original always showed that section); other
  // groups appear only when the user has something in them.
  const categoryEntries = useMemo(
    () =>
      [CATEGORY.ALL, CATEGORY.DASHBOARDS, CATEGORY.PORTALS, CATEGORY.PAGES, CATEGORY.ADMIN, CATEGORY.FAVOURITES]
        .filter((id) => {
          if (id === CATEGORY.ALL || id === CATEGORY.DASHBOARDS) return true;
          if (id === CATEGORY.FAVOURITES) return favoritableItems.length > 0;
          return (counts[id] || 0) > 0;
        })
        .map((id) => ({
          id,
          label: id === CATEGORY.ALL ? "Home" : CATEGORY_LABELS[id],
          icon: CATEGORY_ICONS[id],
          count: id === CATEGORY.ALL ? undefined : counts[id] || 0,
        })),
    [counts, favoritableItems.length]
  );

  const selectCategory = useCallback((id) => {
    setActiveCategory(id);
    setQuery("");
    setSearchOpen(false);
    setMoreOpen(false);
    try {
      window.scrollTo({ top: 0 });
    } catch {
      /* non-browser environment */
    }
  }, []);

  const mobileTabs = categoryEntries
    .filter((entry) => [CATEGORY.ALL, CATEGORY.DASHBOARDS, CATEGORY.PORTALS].includes(entry.id))
    .map((entry) => (entry.id === CATEGORY.PORTALS ? { ...entry, label: "Portals" } : entry));
  const moreEntries = categoryEntries.filter((entry) => MORE_CATEGORY_IDS.includes(entry.id));

  // ─── Profile + logout (existing handlers) ─────────────────────────────────
  const handleLogout = async () => {
    try {
      await logout();
    } catch {
      /* AuthContext.logout already handles its own errors */
    }
    navigate("/");
  };

  const handleProfileUpdate = (updatedUser) => {
    setProfileUser(updatedUser);
    // Same side effects as the Guest Room / Venue dashboards' onUpdate.
    try {
      const existingUser = JSON.parse(localStorage.getItem("user") || "{}");
      localStorage.setItem("user", JSON.stringify({ ...existingUser, ...updatedUser }));
    } catch (error) {
      console.error("Failed to update localStorage:", error);
    }
    window.dispatchEvent(new CustomEvent("userProfileUpdated", { detail: updatedUser }));
  };

  // Original behaviour: single-dashboard users never see the selector.
  if (redirectTo) return <Navigate to={redirectTo} replace />;

  // Analytics view (admin only) — the original in-page view, unchanged.
  if (showAnalytics && isAdmin) {
    return (
      <div className="min-h-screen bg-slate-50 relative">
        <div className="fixed top-4 left-4 z-50">
          <button
            onClick={() => setShowAnalytics(false)}
            className="bg-white border border-slate-200 shadow-lg px-4 py-2 rounded-xl flex items-center gap-2 text-slate-600 hover:text-slate-900 font-medium transition-all hover:scale-105"
          >
            <ArrowRight className="w-4 h-4 rotate-180" /> Back to Dashboard
          </button>
        </div>
        <div className="p-8 pt-16">
          <AdvancedAnalyticsPage />
        </div>
        <EchoOrb onClick={() => setShowEcho(true)} />
        <AnimatePresence>
          {showEcho && <EchoModal open={showEcho} onClose={() => setShowEcho(false)} role={role} userName={userName} />}
        </AnimatePresence>
      </div>
    );
  }

  const moreActive = MORE_CATEGORY_IDS.includes(activeCategory);

  return (
    <div className="cc-ws" data-cc-theme={theme} data-manage={manage ? "true" : "false"} data-search-open={searchOpen ? "true" : "false"}>
      <a className="cc-skip" href="#cc-main">Skip to content</a>

      <SidebarNav
        entries={categoryEntries}
        active={activeCategory}
        onSelect={selectCategory}
        pinned={sidebarPinned}
        onTogglePin={toggleSidebarPinned}
      />

      <div className="cc-main">
        <WorkspaceHeader
          query={query}
          onQueryChange={setQuery}
          searchOpen={searchOpen}
          onSearchOpenChange={setSearchOpen}
          theme={theme}
          onToggleTheme={toggleTheme}
          profile={{
            name: profileUser?.name || userName,
            email: profileUser?.email || email,
            roleText: humanizeRole(role) || "Staff",
            picture: profileUser?.profilePicture,
            onOpenProfile: () => setProfileOpen(true),
            onBack: () => navigate("/"),
            onLogout: handleLogout,
          }}
        />

        <main id="cc-main" className="cc-content" tabIndex={-1}>
          <WelcomeBanner name={profileUser?.name || userName} />

          <div className="cc-toolbar">
            <div className="cc-chips" role="group" aria-label="Filter by category">
              {categoryEntries.map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  type="button"
                  className="cc-chip"
                  aria-pressed={activeCategory === id && !query.trim()}
                  onClick={() => selectCategory(id)}
                >
                  <Icon aria-hidden="true" />
                  {id === CATEGORY.ALL ? "All" : label}
                </button>
              ))}
            </div>
            {favoritableItems.length > 0 && (
              <button type="button" className="cc-manage" aria-pressed={manage} onClick={() => setManage((value) => !value)}>
                <SlidersHorizontal aria-hidden="true" />
                {manage ? "Done" : "Manage"}
              </button>
            )}
          </div>

          <WorkspaceSections
            items={items}
            activeCategory={activeCategory}
            query={query}
            quickAccessItems={quickAccessItems}
            favoritableCount={favoritableItems.length}
            favoriteIds={favoriteIds}
            canFavorite={canFavorite}
            onOpen={openItem}
            onToggleFavorite={toggleFavorite}
            onSelectCategory={selectCategory}
          />
        </main>

        <footer className="cc-footer">Created and Maintained by Office of Student Affairs © 2026</footer>
      </div>

      <MobileBottomNav
        tabs={mobileTabs}
        active={activeCategory}
        moreOpen={moreOpen}
        moreActive={moreActive}
        moreButtonRef={moreButtonRef}
        onSelect={selectCategory}
        onToggleMore={() => setMoreOpen((value) => !value)}
      />
      <MoreSheet
        open={moreOpen}
        entries={moreEntries}
        active={activeCategory}
        onSelect={selectCategory}
        onClose={closeMore}
        returnFocusRef={moreButtonRef}
      />

      <div className="cc-echo-slot">
        <EchoOrb onClick={() => setShowEcho(true)} />
      </div>
      <AnimatePresence>
        {showEcho && <EchoModal open={showEcho} onClose={() => setShowEcho(false)} role={role} userName={userName} />}
      </AnimatePresence>

      <PublicFormsModal open={showPublicForms} onClose={() => setShowPublicForms(false)} />

      {/* Shared ProfileModal, reused as-is; the wrapper only scopes a width fix. */}
      <div className="cc-profile-scope">
        <ProfileModal
          open={profileOpen}
          onClose={() => setProfileOpen(false)}
          currentUser={profileUser}
          onUpdate={handleProfileUpdate}
        />
      </div>
    </div>
  );
};

export default WorkspaceHome;
